import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  TrendingUp, 
  FileCheck, 
  Ship, 
  FileText, 
  Shield, 
  DollarSign, 
  AlertTriangle,
  CheckCircle,
  ArrowRight
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
const ModuleDemoAnimation = lazy(() =>
  import("./ModuleDemoAnimation").then((m) => ({ default: m.ModuleDemoAnimation })),
);
import type { LucideIcon } from "lucide-react";

interface Module {
  icon: LucideIcon;
  titleKey: string;
  subtitleKey: string;
  features: string[];
  detailedFeatures: string[];
  iconColor: string;
  iconBg: string;
}

const getModules = (t: (key: string) => string): Module[] => [
  {
    icon: TrendingUp,
    titleKey: t("module.quote.title"),
    subtitleKey: t("module.quote.subtitle"),
    features: [t("module.quote.f1"), t("module.quote.f2"), t("module.quote.f3")],
    detailedFeatures: [t("module.quote.d1"), t("module.quote.d2"), t("module.quote.d3"), t("module.quote.d4")],
    iconColor: "text-primary",
    iconBg: "bg-primary/10"
  },
  {
    icon: FileCheck,
    titleKey: t("module.rate.title"),
    subtitleKey: t("module.rate.subtitle"),
    features: [t("module.rate.f1"), t("module.rate.f2"), t("module.rate.f3")],
    detailedFeatures: [t("module.rate.d1"), t("module.rate.d2"), t("module.rate.d3"), t("module.rate.d4")],
    iconColor: "text-amber-600",
    iconBg: "bg-amber-500/10"
  },
  {
    icon: Ship,
    titleKey: t("module.book.title"),
    subtitleKey: t("module.book.subtitle"),
    features: [t("module.book.f1"), t("module.book.f2"), t("module.book.f3")],
    detailedFeatures: [t("module.book.d1"), t("module.book.d2"), t("module.book.d3"), t("module.book.d4")],
    iconColor: "text-blue-600",
    iconBg: "bg-blue-500/10"
  },
  {
    icon: FileText,
    titleKey: t("module.docs.title"),
    subtitleKey: t("module.docs.subtitle"),
    features: [t("module.docs.f1"), t("module.docs.f2"), t("module.docs.f3")],
    detailedFeatures: [t("module.docs.d1"), t("module.docs.d2"), t("module.docs.d3"), t("module.docs.d4")],
    iconColor: "text-teal-600",
    iconBg: "bg-teal-500/10"
  },
  {
    icon: Shield,
    titleKey: t("module.customs.title"),
    subtitleKey: t("module.customs.subtitle"),
    features: [t("module.customs.f1"), t("module.customs.f2"), t("module.customs.f3")],
    detailedFeatures: [t("module.customs.d1"), t("module.customs.d2"), t("module.customs.d3"), t("module.customs.d4")],
    iconColor: "text-violet-600",
    iconBg: "bg-violet-500/10"
  },
  {
    icon: DollarSign,
    titleKey: t("module.finance.title"),
    subtitleKey: t("module.finance.subtitle"),
    features: [t("module.finance.f1"), t("module.finance.f2"), t("module.finance.f3")],
    detailedFeatures: [t("module.finance.d1"), t("module.finance.d2"), t("module.finance.d3"), t("module.finance.d4")],
    iconColor: "text-emerald-600",
    iconBg: "bg-emerald-500/10"
  },
  {
    icon: AlertTriangle,
    titleKey: t("module.risk.title"),
    subtitleKey: t("module.risk.subtitle"),
    features: [t("module.risk.f1"), t("module.risk.f2"), t("module.risk.f3")],
    detailedFeatures: [t("module.risk.d1"), t("module.risk.d2"), t("module.risk.d3"), t("module.risk.d4")],
    iconColor: "text-rose-600",
    iconBg: "bg-rose-500/10"
  }
];

export function ModuleShowcase() {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [resumeTimerId, setResumeTimerId] = useState<ReturnType<typeof setTimeout> | null>(null);
  
  const modules = getModules(t);
  
  const handleModuleClick = useCallback((index: number) => {
    if (isAnimating || index === activeIndex) return;
    
    setIsAnimating(true);
    setActiveIndex(index);
    setIsAutoPlaying(false);
    
    setTimeout(() => setIsAnimating(false), 500);
    
    // Clear previous resume timer if exists
    if (resumeTimerId) {
      clearTimeout(resumeTimerId);
    }
    
    // Resume auto-play after 4 seconds
    const newTimerId = setTimeout(() => setIsAutoPlaying(true), 4000);
    setResumeTimerId(newTimerId);
  }, [isAnimating, activeIndex, resumeTimerId]);
  
  // Cleanup resume timer on unmount
  useEffect(() => {
    return () => {
      if (resumeTimerId) {
        clearTimeout(resumeTimerId);
      }
    };
  }, [resumeTimerId]);
  
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % modules.length);
    }, 5000);
    
    return () => clearInterval(timer);
  }, [isAutoPlaying, modules.length]);
  
  const currentModule = modules[activeIndex]!;
  const IconComponent = currentModule.icon;
  
  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            {t("digitalWorker.badge")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t("digitalWorker.title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("digitalWorker.desc")}
          </p>
        </motion.div>
        
        {/* Main Card Container */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="max-w-7xl mx-auto rounded-2xl border border-border/20 bg-card/60 backdrop-blur-lg shadow-lg overflow-hidden"
        >
          <div className="flex flex-col lg:flex-row">
            {/* Left Sidebar - Module List */}
            <div className="w-full lg:w-[280px] bg-card/90 border-b lg:border-b-0 lg:border-r border-border/20 p-4">
              <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
                {modules.map((module, index) => {
                  const ModuleIcon = module.icon;
                  const isActive = index === activeIndex;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => handleModuleClick(index)}
                      className={`group relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 min-w-[200px] lg:min-w-0 lg:w-full text-left overflow-hidden
                        ${isActive 
                          ? "bg-secondary text-secondary-foreground shadow-md shadow-secondary/20" 
                          : "hover:translate-x-1 border border-transparent"
                        }`}
                    >
                      {/* Hover gradient background - light green gradient */}
                      <div className={`absolute inset-0 rounded-xl bg-gradient-to-r from-secondary/20 via-secondary/10 to-secondary/5 opacity-0 transition-opacity duration-300 pointer-events-none
                        ${!isActive ? "group-hover:opacity-100" : ""}`} 
                      />
                      
                      {/* Icon with unique color */}
                      <div className={`relative z-10 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300
                        ${isActive 
                          ? "bg-secondary-foreground/20 text-secondary-foreground" 
                          : `${module.iconBg} ${module.iconColor} group-hover:scale-110`
                        }`}
                      >
                        <ModuleIcon className="w-4.5 h-4.5" />
                      </div>
                      
                      {/* Text */}
                      <div className="relative z-10 flex-1 min-w-0">
                        <div className={`font-semibold text-sm truncate transition-colors duration-300 ${isActive ? "text-secondary-foreground" : "text-foreground/90 group-hover:text-secondary"}`}>
                          {module.titleKey}
                        </div>
                        <div className={`text-xs truncate transition-colors duration-300 ${isActive ? "text-secondary-foreground/80" : "text-muted-foreground"}`}>
                          {module.subtitleKey}
                        </div>
                      </div>
                      
                      {/* Active Indicator */}
                      <div className={`relative z-10 w-1 rounded-full transition-all duration-300 ${
                        isActive 
                          ? "h-6 bg-secondary-foreground opacity-100" 
                          : "h-0 opacity-0 group-hover:h-4 group-hover:opacity-70 bg-secondary"
                      }`} />
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Right Content Area */}
            <div className="flex-1 flex flex-col md:flex-row">
              {/* Feature Details */}
              <div className="w-full md:w-[260px] p-6 bg-card/50 border-b md:border-b-0 md:border-r border-border/20">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, y: 12, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -12, scale: 0.98 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="space-y-5"
                  >
                    {/* Module Icon & Title */}
                    <motion.div
                      initial={{ scale: 0.8, rotate: -10 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                      className={`w-14 h-14 rounded-2xl ${currentModule.iconBg} flex items-center justify-center`}
                    >
                      <IconComponent className={`w-7 h-7 ${currentModule.iconColor}`} />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05, duration: 0.35 }}
                    >
                      <h3 className="text-xl font-bold text-foreground">{currentModule.titleKey}</h3>
                      <p className="text-sm text-muted-foreground">{currentModule.subtitleKey}</p>
                    </motion.div>
                    
                    {/* Feature Tags */}
                    <div className="flex flex-wrap gap-2">
                      {currentModule.features.map((feature, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, scale: 0.9, y: 4 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ delay: 0.15 + i * 0.06, duration: 0.3 }}
                          className="px-2.5 py-1 bg-muted/80 text-muted-foreground text-xs rounded-full"
                        >
                          {feature}
                        </motion.span>
                      ))}
                    </div>
                    
                    {/* Detailed Features */}
                    <div className="space-y-2.5">
                      {currentModule.detailedFeatures.map((feature, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + i * 0.08, duration: 0.35 }}
                          className="flex items-center gap-2 text-sm"
                        >
                          <CheckCircle className={`w-4 h-4 flex-shrink-0 ${currentModule.iconColor}`} />
                          <span className="text-foreground/80">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* CTA Button */}
                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="flex items-center gap-2 text-sm text-primary font-medium hover:gap-3 transition-all duration-300 group"
                    >
                      <span>{t("services.learnMore")}</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                  </motion.div>
                </AnimatePresence>
              </div>
              
              {/* Demo Animation Area */}
              <div className="flex-1 p-5 bg-muted/60 dark:bg-muted/40 min-h-[360px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full"
                  >
                    <Suspense fallback={<div className="h-full min-h-[320px]" />}>
                      <ModuleDemoAnimation moduleIndex={activeIndex} />
                    </Suspense>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
